<template>
  <v-flex column>
    <v-parallax
      src="https://gm.goz.fun/wp-content/uploads/2023/03/checkout-cover.png"
      height="400"
      role="img"
      aria-label="Checkout background artwork"
    >
      <v-layout
        column
        align-center
        justify-center
      >
        <h1
          class="white--text"
        >
          {{ $t("components.checkout.title") }}
        </h1>
        <h2
          class="white--text"
        >
          {{ $t("components.checkout.subtitle") }}
        </h2>
      </v-layout>
    </v-parallax>

    <v-card
      v-show="!hasConfigLoaded"
      flat
      height="250px"
    >
      <v-container>
        <v-layout
          column
          align-center
          justify-center
        >
          <v-progress-circular
            :size="50"
            color="accent"
            indeterminate
            role="status"
            aria-label="Loading payment options"
          />
        </v-layout>
      </v-container>
    </v-card>

    <v-card
      v-show="hasConfigLoaded"
      flat
    >
      <v-toolbar color="primary">
        <v-toolbar-title class="white--text">
          <h3>
            {{ $t("components.checkout.selectPoints") }}
          </h3>
        </v-toolbar-title>
      </v-toolbar>

      <router-view
        role="region"
        aria-live="polite"
        aria-label="Checkout content section"
      />

      <v-card-text>
        <v-container fluid>
          <v-flex
            v-show="!isLoading"
            column
            align-center
            justify-center
          >
            <v-radio-group
              v-model="currentCost"
              column
              aria-label="Available point packages"
              role="combobox"
            >
              <v-radio
                v-for="(paymentPoints, paymentPrice) in paymentPrices"
                :key="paymentPoints"
                :value="paymentPrice"
                :label="setProductName(paymentPrice, paymentPoints)"
                color="accent"
                name="points-price"
                @change="onPriceChange(paymentPrice, paymentPoints)"
              />
            </v-radio-group>
          </v-flex>
        </v-container>
      </v-card-text>

      <v-card>
        <v-alert
          v-if="!hasReachedPayPalQualifiedAmount"
          color="info"
          icon="info"
          role="alert"
          aria-live="polite"
        >
          {{ $t("components.checkout.restrictions.unableToUsePayPalMinAmount") }}
        </v-alert>

        <v-alert
          color="info"
          icon="info"
          role="alert"
          aria-live="polite"
        >
          {{ $t("components.checkout.revolut") }}
        </v-alert>

        <stripe-checkout
          v-if="isStripeEnabled"
          ref="checkoutStripeRef"
          locale="en"
          :mode="stripeMode"
          :pk="getStripePk"
          :line-items="stripeLineItems"
          :customer-email="email"
          :client-reference-id="String(order.id)"
          :success-url="stripeSuccessUrl"
          :cancel-url="stripeCancelUrl"
          :session-id="session.id"
          @loading="v => isStripeLoading = v"
        />

        <v-toolbar color="primary">
          <v-toolbar-title class="white--text">
            <h3>
              {{ $t("components.checkout.payments") }}
            </h3>
          </v-toolbar-title>
          <v-spacer />

          <v-btn
            v-if="!hasStripelRestriction && isStripeEnabled"
            v-show="!isLoading"
            color="accent"
            aria-label="Pay with Stripe"
            role="button"
            @click="checkoutStripeOneTimePayment"
          >
            <v-icon large>
              fab fa-stripe
            </v-icon>
          </v-btn>

          <div
            v-if="isPayPalEnabled && !hasPayPalRestriction && hasReachedPayPalQualifiedAmount"
            v-show="!isLoading"
            id="paypal-button-container"
          />

          <v-spacer />
        </v-toolbar>

        <v-toolbar color="primary">
          <v-toolbar-title class="white--text">
            <h3>
              {{ $t("components.checkout.supportus.title") }}
            </h3>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            aria-label="Donate using PayPal"
            color="accent"
            role="button"
            @click="donateForFree"
          >
            {{ $t("components.checkout.supportus.buttons.paypal") }}
          </v-btn>
        </v-toolbar>

        <!-- Alerts -->
        <div
          v-for="(errorsGroup, errorsGroupIndex) in filteredErrors"
          :key="errorsGroupIndex"
          aria-live="polite"
        >
          <v-alert
            v-for="error in errorsGroup"
            :key="error.id"
            :type="error.type"
            :icon="error.icon"
            role="alert"
          >
            {{ error.msg }}
          </v-alert>
        </div>

        <v-progress-linear
          v-show="isLoading"
          color="accent"
          indeterminate
          role="status"
          aria-label="Processing payment"
        />

        <v-spacer />

        <v-card-title class="pb-0">
          <h3>
            {{ $t("components.checkout.bundleTitle") }}
          </h3>
        </v-card-title>

        <v-card-title class="display-5 pt-1 pb-0 font-weight-thin">
          <span>
            {{ $t("components.checkout.bundleInfo", { total, points, currency: currencySymbol }) }}
          </span>
        </v-card-title>

        <v-spacer />
        <v-divider />

        <v-list dense>
          <v-card-title class="display-5 pt-1 pb-0 font-weight-thin">
            <h3>
              {{ $t("components.checkout.evaluating") }}
            </h3>
          </v-card-title>

          <v-list-item>
            <v-list-item-content class="font-weight-thin">
              {{ $t("components.checkout.gold") }}
            </v-list-item-content>
            <v-list-item-content class="align-center">
              <v-chip style="min-width: 120px">
                <v-avatar>
                  <img
                    src="https://gm.goz.fun/wp-content/uploads/2023/03/gold-icon.png"
                    alt="Gold currency icon"
                  >
                </v-avatar>
                {{ gold }}
              </v-chip>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content class="font-weight-thin">
              {{ $t("components.checkout.silver") }}
            </v-list-item-content>
            <v-list-item-content class="align-center">
              <v-chip style="min-width: 120px">
                <v-avatar>
                  <img
                    src="https://gm.goz.fun/wp-content/uploads/2023/03/silver-icon.png"
                    alt="Silver currency icon"
                  >
                </v-avatar>
                {{ silver }}
              </v-chip>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-card>

    <!-- Dialog already has title, persistent logic and aria-modal set implicitly by Vuetify -->
    <v-dialog
      v-model="isRestrictedPaymentsDialogVisible"
      max-width="700"
      :persistent="hasPayPalRestriction && hasStripelRestriction"
      transition="dialog-bottom-transition"
      :fullscreen="$vuetify.breakpoint.mdAndDown"
      :width="$vuetify.breakpoint.mdAndDown ? 'auto' : '700'"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('components.checkout.restrictions.dialog.title')"
      aria-labelledby="paymentRestrictionDialogTitle"
    >
      <v-card>
        <v-toolbar color="secondary">
          <v-toolbar-title id="paymentRestrictionDialogTitle">
            {{ $t("components.checkout.restrictions.dialog.title") }}
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <p>{{ $t("components.checkout.restrictions.dialog.description") }}</p>

          <v-list
            three-line
            subheader
          >
            <v-subheader>
              {{ $t("components.checkout.restrictions.dialog.subtitle") }}
            </v-subheader>

            <v-list-item v-if="hasPayPalRestriction">
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("components.checkout.buttons.paypal") }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t("components.checkout.restrictions.dialog.paypal") }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="hasStripelRestriction">
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("components.checkout.buttons.stripe") }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t("components.checkout.restrictions.dialog.stripe") }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-divider />
          <v-subheader>
            {{ $t("components.checkout.restrictions.dialog.contact") }}
          </v-subheader>
        </v-card-text>

        <v-card-actions>
          <v-btn
            aria-label="Contact via Facebook Page"
            flat
            color="accent"
            role="link"
            @click="toLink(communities.fbPage)"
          >
            {{ $t("components.checkout.restrictions.dialog.buttons.fbPage") }}
          </v-btn>

          <v-btn
            aria-label="Join Discord"
            flat
            color="accent"
            role="link"
            @click="toLink(communities.discord)"
          >
            {{ $t("components.checkout.restrictions.dialog.buttons.discord") }}
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="hasPayPalRestriction && hasStripelRestriction"
            aria-label="Go to Profile"
            role="link"
            color="accent"
            flat
            @click="toProfile()"
          >
            {{ $t("components.checkout.restrictions.dialog.buttons.profile") }}
          </v-btn>

          <v-btn
            v-if="!(hasPayPalRestriction && hasStripelRestriction)"
            color="accent"
            aria-label="Continue"
            role="button"
            flat
            @click.stop="setRestrictedPaymentsDialogVisiblity(false)"
          >
            {{ $t("components.checkout.restrictions.dialog.buttons.continue") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import { mapGetters } from "vuex";
import { StripeCheckout } from '@vue-stripe/vue-stripe';
import { filterByKeys } from "@/utils/object.utils";
import {
    toCurrencySymbol,
    toGoldPrice,
    toSilverPrice
} from "@/utils/money.utils";
import { csrfToken } from "@/utils/global.utils";
import config from "@/constants/config";
import communityLinks from "@/constants/links";

export default {
    name: "CheckoutLayout",
    components: {
        StripeCheckout,
    },
    inject: ["$validator"],
    data() {
        return {
            currentCost: 0,
            isLoading: false,
            isStripeLoading: false,
            isRestrictedPaymentsDialogVisible: false,
            order: {
                id: 0
            },
            isIneteralPaymentCalled: false,
            isRedirectPaymentCalled: false,
            communities: communityLinks.communities,
            stripeToken: null,
            stripeLineItems: [],
            stripeMode: 'payment',
            session: {},
            product: {
                name: '',
                description: '',
            }
        }
    },
    computed: {
        ...mapGetters([
            "email",
            "getStripeSku",
            "getStripePk",
            "recommendedPrices",
            "hasPayPalRestriction",
            "hasStripelRestriction",
            "hasReachedPayPalQualifiedAmount",
            "paymentPrices",
            "paymentCurrency",
            "isPayPalEnabled",
            "isStripeEnabled",
            "pricesEUR",
            "getPayPalConfig"
        ]),
        gold() {
            return toGoldPrice(
                this.currentCost,
                this.paymentPrices,
                this.$store.getters.goldRate,
                this.$store.getters.pointsMultipliedBy
            );
        },
        silver() {
            return toSilverPrice(
                this.currentCost,
                this.paymentPrices,
                this.$store.getters.silverRate,
                this.$store.getters.pointsMultipliedBy
            );
        },
        filteredErrors() {
            return filterByKeys(this.$validator.errors.collect(), []);
        },
        token() {
            return csrfToken();
        },
        // PayPal button style
        btnPayPalStyle() {
            return {
                color: "white",
                shape: "rect",
                size: "responsive",
                layout: "horizontal"
            };
        },
        PayPalWebhookUrl() {
            return `${this.getPayPalConfig.notify_url}/${this.order.id}`;
        },
        total() {
            return parseFloat(this.currentCost || this.recommendedPrices);
        },
        points() {
            return parseInt(this.paymentPrices[this.currentCost]);
        },
        hasConfigLoaded() {
            return this.paymentPrices[this.currentCost];
        },

        currencySymbol(){
            return toCurrencySymbol(this.paymentCurrency)
        },

        stripeSuccessUrl() {
            return `${process.env.MIX_APP_URL}${this.$router.resolve({
                name: "client.checkout.completed",
                params: {
                    provider: "stripe",
                    id: this.order.id
                }
            }).href}`;
        },

        stripeCancelUrl() {
            return `${process.env.MIX_APP_URL}${this.$router.resolve({
                name: "client.checkout.cancelled",
                params: {
                    provider: "stripe",
                    id: this.order.id
                }
            }).href}`;
        },
    },
    created() {
        const isDebug = this.getPayPalConfig.mode === 'sandbox' ? 'true':'false'
        const scriptPayPalOneTimePayment = document.createElement('script');
        scriptPayPalOneTimePayment.src = `https://www.paypal.com/sdk/js?client-id=${this.getPayPalConfig.client[this.getPayPalConfig.mode]}&locale=en_US&currency=${this.getPayPalConfig.currency}&debug=${isDebug}&enable-funding=card,credit,paylater,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort&intent=authorize`;

        document.body.appendChild(scriptPayPalOneTimePayment);

        scriptPayPalOneTimePayment.addEventListener('load', () => {
            this.renderPayPalOneTimePayment();
        })
    },
    beforeMount() {
        if (!this.currentCost) {
            this.currentCost = this.recommendedPrices;
        }

        this.setRestrictedPaymentsDialogVisiblity(
            this.hasPayPalRestriction ||
                this.hasStripelRestriction
        );
        this.createOrder();
        window.addEventListener("beforeunload", this.onUnload, false);
    },
    beforeUnmount() {
        this.$api.doPayPalDeleteOrder(this.order.id);
        window.removeEventListener("beforeunload", this.onUnload, false);
        window.removeEventListener("beforeunload", this.onUnload, false);
    },
    methods: {
        onUnload() {
            this.deleteOrder();
        },
        setProductName(price, points) {
            return `${toCurrencySymbol(
                this.paymentCurrency
            )}${price} or ${toCurrencySymbol('EUR')} ${this.pricesEUR[price]} for ${points} ${this.$t(
                "components.checkout.currenyName"
            )}`;
        },
        setProductDescription(price, points) {
            return `Get ${toCurrencySymbol(
                this.paymentCurrency
            )}${price} or ${toCurrencySymbol('EUR')} ${this.pricesEUR[price]} for ${points} ${this.$t(
                "components.checkout.currenyName"
            )} and start benefit more in-game!`;
        },

        checkout(provider) {
            // check if has any price set
            if (this.currentCost) {
                this.isLoading = true;

                // track for all payments
                // this.$ga.event(
                //     "begin-checkout",
                //     "checkout-price",
                //     String(this.currentCost)
                // );
                // this.$ga.event(
                //     "begin-checkout",
                //     "checkout-points",
                //     String(this.points)
                // );
                // this.$ga.event("begin-checkout", "checkout-provider", provider);
            }
        },
        // Create order for paypal
        createOrder() {
            const price = this.currentCost || this.recommendedPrices;

            this.product.description = this.setProductDescription(price, this.points);
            this.product.name = this.setProductName(price, this.points);

            this.isLoading = true;
            this.$api
                .doPayPalCheckout(price)
                .then(({ order }) => {
                    this.isIneteralPaymentCalled = false;
                    this.order = order;
                    // Disable loading
                    this.isLoading = false;
                });
        },
        deleteOrder() {
            if (!this.isRedirectPaymentCalled) {
                this.$api.doPayPalDeleteOrder(this.order.id);
            }
        },
        // On redirection is completed for paypal
        onRedirectComplete(provider) {
            // Clean current cost
            this.currentCost = 0;
            // Recreate another order
            this.createOrder();

            if (provider == "paypal") {
                setTimeout(() => {
                    window.location.reload();
                }, 5000)

                return;
            }
        },
        // Set dialog visiblity to true/false
        setRestrictedPaymentsDialogVisiblity(visible) {
            this.isRestrictedPaymentsDialogVisible = visible;
        },
        /**
         * Go to link externally
         */
        toLink(link) {
            if (link) {
                window.open(link);
            }
        },
        /**
         * Go back to profile
         */
        toProfile() {
            // return to profile overview
            this.$router.push({
                name: "client.profile.overview"
            });
        },
        // On price change update paypal order price
        onPriceChange(price, points) {
            // Update order price
            if (!this.isIneteralPaymentCalled) {
                this.isLoading = true;
                this.$api
                    .doPayPalUpdateOrder(this.order.id, price)
                    .then(({ order }) => {
                        this.order = order;
                        this.isLoading = false;

                        this.product.description = this.setProductDescription(price, points);
                        this.product.name = this.setProductName(price, points);

                        // return back always just in case user still on completed/cancelled
                        this.$router.push({
                            name: "client.checkout",
                            replace: true,
                            query: {
                                t: new Date().getTime(),
                            }
                        });
                    });
            }

            // this.$ga.event("selected-price", "checkout-price", String(price));
            // this.$ga.event(
            //     "selected-points",
            //     "checkout-points",
            //     String(points)
            // );
        },
        // On completed paypal order this will be called
        onPayPalAuthorizedPayment(paypalOrderId, authId) {
            this.$api.onAuthorizedPayPalCheckout(this.PayPalWebhookUrl, paypalOrderId, authId)
            .then(() => {
                this.$router.push(
                    {
                        name: "client.checkout.completed",
                        params: {
                            provider: "paypal",
                            id: this.order.id
                        },
                        query: {
                            t: new Date().getTime(),
                        },
                        replace: true
                    },
                    this.onRedirectComplete.bind(this, "paypal")
                );
            })
        },


        renderPayPalOneTimePayment() {
            // Render the PayPal button into #paypal-button-container
            paypal.Buttons({
                style: this.btnPayPalStyle,

                onInit: (data, actions) => {

                },

                onClick: () => {
                    this.isIneteralPaymentCalled = true;
                    this.checkout("paypal");
                },
                // Set up the transaction
                createOrder: (_data, actions) => {
                    this.product.description = this.setProductDescription(this.order.amount, this.points);
                    this.product.name = this.setProductName(this.order.amount, this.points);

                    return actions.order.create({
                        intent:  "AUTHORIZE",
                        is_final_capture: true,
                        purchase_units: [
                            {
                                description: this.product.description,
                                amount: {
                                    currency_code: this.getPayPalConfig.currency,
                                    value: this.order.amount,
                                    breakdown: {
                                        item_total: {
                                            currency_code: this.getPayPalConfig.currency,
                                            value: this.order.amount,
                                        }
                                    }
                                },
                                items: [
                                    {
                                        sku: "GOZ-DONATE",
                                        name: this.product.name, /* Shows within upper-right dropdown during payment approval */
                                        description: this.product.description, /* Item details will also be in the completed paypal.com transaction view */
                                        unit_amount: {
                                            currency_code: this.getPayPalConfig.currency,
                                            value: this.order.amount,
                                        },
                                        quantity: 1,
                                        category: "DONATION",
                                    },
                                ],
                                custom: this.getPayPalConfig.custom,
                                custom_id: this.getPayPalConfig.custom,
                            },
                        ],
                        application_context: {
                            brand_name: 'Gods of Zushin',
                            return_url: this.getPayPalConfig.return_cancel,
                            cancel_url: this.getPayPalConfig.return_url
                        },
                        payer: {
                            email_address: this.email,
                        },
                        ...this.getPayPalConfig.payee.email &&  this.getPayPalConfig.payee.merchantId ? {
                            payee: {
                                email_address: this.getPayPalConfig.payee.email,
                                merchant_id: this.getPayPalConfig.payee.merchantId,
                            }
                        } : {}
                    });
                },

                // Finalize the transaction
                onApprove: (data, actions) => {
                    return actions.order.authorize().then((authorization) => {

                        const authorizationID = authorization.purchase_units[0].payments.authorizations[0].id
                        // Full available details
                        console.log('Capture result', authorization, JSON.stringify(authorization, null, 2));

                        this.onPayPalAuthorizedPayment(data.orderID, authorizationID)
                    });
                },

                onError: (err) => {
                    console.error(err);
                },

            }).render('#paypal-button-container');
        },


        checkoutStripeOneTimePayment() {
            this.stripeMode = 'payment';
            this.stripeLineItems = [
                {
                    price: this.getStripeSku[this.stripeMode][this.order.amount],
                    quantity: 1
                }
            ];

            this.checkoutStripe()
        },
        checkoutStripeSubscribePayment() {
            this.stripeMode = 'subscription';
            this.stripeLineItems = [
                {
                    price: this.getStripeSku[this.stripeMode][this.order.amount],
                    quantity: 1
                }
            ];

            this.checkoutStripe()
        },
        donateForFree() {
            window.location.href = "https://www.paypal.com/donate/?hosted_button_id=DNE9M6HBGPSKY";
        },
        async checkoutStripe() {
            const { status, session } = await this.$api.doStripeCheckout(this.order.id, this.stripeMode)

            this.session = session;

            setTimeout(() => {
                if (status == 200) {
                    // If payment is called set set currently is a redirect payment
                    this.isRedirectPaymentCalled = true;
                    // We start the process
                    this.$refs.checkoutStripeRef.redirectToCheckout();
                } else {
                    console.error('Something went wrong while doing stripe checkout')
                }
            }, 250)
        },
    }
};
</script>
